"use client";

import { useState, useCallback } from "react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { type ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import {
  Pencil, Eye, Loader2, Star,
  Mail, KeyRound, ShieldCheck, Info, Settings2, Video,
  GraduationCap, Package2, MoreHorizontal, Check,
  StickyNote, Plus, Trash2, X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { DataTable, DataTableColumnHeader, DataTableStatusCell, type DataTableConfig } from "@admin/_components/data-table";
import { useApiList, useApiPost } from "@admin/_lib/api-hooks";
import { useQueryClient, useQuery } from "@tanstack/react-query";

// ── Types ─────────────────────────────────────────────────────────────────────

interface UserRow {
  _id: string;
  name: string;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  user_type: string;
  status: number;
  createdon_datetime?: number;
  created_at?: number;
  last_login_time?: number;
  /** 0 = no access, 1 = read access, 2 = read+write access */
  mundane_read_write_access?: 0 | 1 | 2;
  /** 0 or false = sequential off, 1 or true = sequential on */
  sequential_training_enabled?: 0 | 1 | boolean;
  /** Diviner mystery school completion percentage (0–100) */
  diviner_mystery_percentage?: number;
}

const roleLabel: Record<string, string> = {
  is_admin: "Admin",
  is_astrologer: "Astrologer",
  is_tarotreader: "Tarot Reader",
  is_customer: "Customer",
  is_social_advo: "Social Advocate",
  is_customer_socialadvo: "Customer + Social",
  is_affiliate: "Affiliate",
  is_Perennial_Mandalism: "Perennial",
  is_mystery_school: "Mystery School",
  is_diviner: "Diviner",
  is_trainee: "Trainee",
  is_astrologer_tarotreader: "Astro + Tarot",
};

// ── Shared ────────────────────────────────────────────────────────────────────

function useBaseUrl() {
  return process.env.NEXT_PUBLIC_API_CLOUDFRONT ?? "https://api.divineinfinitebeing.com/";
}

function modalStyles() {
  return { background: "hsl(240 28% 14%)", border: "1px solid hsl(240 20% 22%)" };
}

function CloseButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-3 w-full rounded-lg py-2 text-xs text-muted-foreground hover:text-foreground transition-colors border"
      style={{ borderColor: "hsl(240 20% 26%)" }}
    >
      Close
    </button>
  );
}

// ── Note type ─────────────────────────────────────────────────────────────────

interface AdminNote {
  _id: string;
  note: string;
  user_id?: string;
  created_at?: number;
  createdon_datetime?: number;
}

// ── User Notes Dialog (D-09) ──────────────────────────────────────────────────
// Per-user notes: list/add/edit/delete
// Endpoints: notes/listnotedata · notes/addnotedata · notes/editnotedata · notes/deletenotedata

function UserNotesDialog({
  user,
  onClose,
}: {
  user: { id: string; name: string } | null;
  onClose: () => void;
}) {
  const { data: session } = useSession();
  const queryClient = useQueryClient();
  const baseUrl = useBaseUrl();
  const [newNote, setNewNote] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState("");
  const [savingNew, setSavingNew] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data: notes, isLoading: notesLoading } = useQuery<AdminNote[]>({
    queryKey: ["admin-user-notes", user?.id],
    queryFn: async () => {
      const res = await fetch(`${baseUrl}notes/listnotedata`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token ?? ""}` },
        body: JSON.stringify({ condition: { user_id: user?.id }, sort: { createdon_datetime: 1 }, limit: 100, skip: 0 }),
      });
      const data = await res.json();
      return data.results ?? data.data ?? [];
    },
    enabled: !!user && !!session?.token,
    staleTime: 0,
  });

  async function addNote() {
    if (!user || !newNote.trim()) return;
    setSavingNew(true);
    try {
      const res = await fetch(`${baseUrl}notes/addnotedata`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token ?? ""}` },
        body: JSON.stringify({ user_id: user.id, note: newNote.trim() }),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success("Note added");
      setNewNote("");
      void queryClient.invalidateQueries({ queryKey: ["admin-user-notes", user.id] });
    } catch { toast.error("Failed to add note"); }
    finally { setSavingNew(false); }
  }

  async function saveEdit(noteId: string) {
    if (!editingText.trim()) return;
    setSavingEdit(true);
    try {
      const res = await fetch(`${baseUrl}notes/editnotedata`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token ?? ""}` },
        body: JSON.stringify({ _id: noteId, note: editingText.trim() }),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success("Note updated");
      setEditingId(null);
      void queryClient.invalidateQueries({ queryKey: ["admin-user-notes", user?.id] });
    } catch { toast.error("Failed to update note"); }
    finally { setSavingEdit(false); }
  }

  async function deleteNote(noteId: string) {
    setDeletingId(noteId);
    try {
      const res = await fetch(`${baseUrl}notes/deletenotedata`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token ?? ""}` },
        body: JSON.stringify({ _id: noteId }),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success("Note deleted");
      void queryClient.invalidateQueries({ queryKey: ["admin-user-notes", user?.id] });
    } catch { toast.error("Failed to delete note"); }
    finally { setDeletingId(null); }
  }

  return (
    <Dialog open={!!user} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col" style={modalStyles()}>
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <StickyNote className="size-4" style={{ color: "#ffa800" }} />
            Notes — {user?.name}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Add and manage admin notes for this user.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-2 py-1 pr-1">
          {notesLoading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground text-sm">
              <Loader2 className="size-4 animate-spin" /> Loading notes...
            </div>
          ) : !notes?.length ? (
            <div className="rounded-lg px-4 py-8 text-center text-sm text-muted-foreground"
              style={{ border: "1px dashed hsl(240 20% 26%)" }}>
              No notes yet. Add one below.
            </div>
          ) : (
            notes.map((n) => {
              const ts = n.createdon_datetime ?? n.created_at;
              const isEditing = editingId === n._id;
              return (
                <div key={n._id} className="rounded-lg p-3 text-sm space-y-1.5"
                  style={{ background: "hsl(240 28% 11%)", border: "1px solid hsl(240 20% 22%)" }}>
                  {isEditing ? (
                    <>
                      <textarea
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        rows={3}
                        className="w-full rounded-md px-3 py-2 text-sm text-foreground resize-none focus:outline-none"
                        style={{ background: "hsl(240 28% 16%)", border: "1px solid hsl(240 20% 28%)" }}
                      />
                      <div className="flex gap-1.5 justify-end">
                        <button type="button" onClick={() => setEditingId(null)}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors hover:bg-white/5"
                          style={{ color: "hsl(var(--muted-foreground))", border: "1px solid hsl(240 20% 26%)" }}>
                          <X className="size-3" /> Cancel
                        </button>
                        <button type="button" onClick={() => void saveEdit(n._id)}
                          disabled={savingEdit || !editingText.trim()}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded font-semibold transition-colors disabled:opacity-60"
                          style={{ background: "rgba(255,168,0,0.15)", border: "1px solid rgba(255,168,0,0.4)", color: "#ffa800" }}>
                          {savingEdit ? <Loader2 className="size-3 animate-spin" /> : null} Save
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-foreground leading-relaxed">{n.note}</p>
                      <div className="flex items-center justify-between">
                        {ts ? <span className="text-[10px] text-muted-foreground">{format(new Date(ts), "MMM d, yyyy")}</span> : null}
                        <div className="flex gap-1">
                          <button type="button" onClick={() => { setEditingId(n._id); setEditingText(n.note); }}
                            className="inline-flex size-6 items-center justify-center rounded transition-colors hover:bg-white/8 text-muted-foreground hover:text-foreground">
                            <Pencil className="size-3" />
                          </button>
                          <button type="button" onClick={() => void deleteNote(n._id)}
                            disabled={deletingId === n._id}
                            className="inline-flex size-6 items-center justify-center rounded transition-colors hover:bg-red-500/10 text-muted-foreground hover:text-red-400">
                            {deletingId === n._id ? <Loader2 className="size-3 animate-spin" /> : <Trash2 className="size-3" />}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Add new note */}
        <div className="shrink-0 pt-2 space-y-2">
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            rows={3}
            placeholder="Write a note..."
            className="w-full rounded-md px-3 py-2 text-sm text-foreground resize-none focus:outline-none"
            style={{ background: "hsl(240 28% 11%)", border: "1px solid hsl(240 20% 26%)" }}
          />
          <div className="flex gap-2">
            <button type="button" onClick={() => void addNote()}
              disabled={savingNew || !newNote.trim()}
              className="flex-1 py-2 text-xs rounded-lg font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ background: "rgba(255,168,0,0.15)", border: "1px solid rgba(255,168,0,0.4)", color: "#ffa800" }}>
              {savingNew ? <Loader2 className="size-3.5 animate-spin" /> : <Plus className="size-3.5" />}
              Add Note
            </button>
            <CloseButton onClick={onClose} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Mundane Astrology Activation Dialog ───────────────────────────────────────

function MundaneAstrologyDialog({
  user,
  onClose,
}: {
  user: { id: string; name: string; access: 0 | 1 | 2 } | null;
  onClose: () => void;
}) {
  const { data: session } = useSession();
  const queryClient = useQueryClient();
  const baseUrl = useBaseUrl();
  const [loading, setLoading] = useState<"read" | "write" | null>(null);

  async function grantAccess(type: "read" | "write") {
    if (!user) return;
    setLoading(type);
    try {
      const res = await fetch(`${baseUrl}user/mundane-astrlogy-read-write-access`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token ?? ""}` },
        body: JSON.stringify({ user_id: user.id, mundane_read_write_access: type === "read" ? 1 : 2 }),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success(`Mundane astrology ${type} access granted to ${user.name}`);
      void queryClient.invalidateQueries({ queryKey: ["user/user-list"] });
      onClose();
    } catch {
      toast.error("Failed to update mundane astrology access");
    } finally {
      setLoading(null);
    }
  }

  const accessLabel: Record<0 | 1 | 2, string> = { 0: "No access", 1: "Read access", 2: "Read + Write access" };

  return (
    <Dialog open={!!user} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-sm" style={modalStyles()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="size-4" style={{ color: "#ffa800" }} />
            Mundane Astrology Access
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-xs">
            Grant access to mundane astrology features for{" "}
            <span className="font-semibold text-foreground">{user?.name}</span>.
            {user?.access ? (
              <span className="block mt-1">
                Current:{" "}
                <Badge className="text-[10px] border ml-1"
                  style={{ background: "rgba(255,168,0,0.1)", color: "#ffa800", borderColor: "rgba(255,168,0,0.3)" }}>
                  {accessLabel[user.access]}
                </Badge>
              </span>
            ) : null}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <button type="button" disabled={loading !== null} onClick={() => void grantAccess("read")}
            className="flex flex-col items-center gap-2 rounded-xl p-4 border transition-colors disabled:opacity-60"
            style={{ background: "rgba(37,172,235,0.08)", borderColor: "rgba(37,172,235,0.3)", color: "#25aceb" }}>
            {loading === "read" ? <Loader2 className="size-5 animate-spin" /> : <Star className="size-5" />}
            <span className="text-sm font-semibold">Read Access</span>
            <span className="text-[10px] text-muted-foreground text-center">Can view mundane astrology data</span>
          </button>
          <button type="button" disabled={loading !== null} onClick={() => void grantAccess("write")}
            className="flex flex-col items-center gap-2 rounded-xl p-4 border transition-colors disabled:opacity-60"
            style={{ background: "rgba(255,168,0,0.08)", borderColor: "rgba(255,168,0,0.3)", color: "#ffa800" }}>
            {loading === "write" ? <Loader2 className="size-5 animate-spin" /> : <Star className="size-5 fill-current" />}
            <span className="text-sm font-semibold">Write Access</span>
            <span className="text-[10px] text-muted-foreground text-center">Can view + manage mundane astrology</span>
          </button>
        </div>
        <CloseButton onClick={onClose} />
      </DialogContent>
    </Dialog>
  );
}

// ── User Preview Dialog ───────────────────────────────────────────────────────

function UserPreviewDialog({ userId, onClose }: { userId: string | null; onClose: () => void }) {
  const { data: session } = useSession();
  const baseUrl = useBaseUrl();

  const { data, isLoading } = useQuery({
    queryKey: ["user/preview", userId],
    queryFn: async () => {
      const res = await fetch(`${baseUrl}user/user-preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token ?? ""}` },
        body: JSON.stringify({ _id: userId }),
      });
      if (!res.ok) throw new Error("Failed to fetch user details");
      const json = await res.json();
      return json.results ?? json.result ?? json;
    },
    enabled: !!userId && !!session?.token,
  });

  const pct = (v: unknown) => (v != null ? `${v}%` : "—");

  return (
    <Dialog open={!!userId} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md" style={modalStyles()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="size-4 text-[#16a085]" />
            User Preview
          </DialogTitle>
          <DialogDescription className="text-xs">Detailed profile for this user.</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-12 flex justify-center"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>
        ) : data ? (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              {[
                ["Name", data.name],
                ["Email", data.email],
                ["Phone", data.phone],
                ["City", data.city],
                ["State", data.state],
                ["Zip", data.zip],
              ].map(([label, val]) => (
                <div key={label} className={label === "Email" ? "col-span-2" : undefined}>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
                  <p className="text-sm">{val || "—"}</p>
                </div>
              ))}
            </div>

            {/* Packages */}
            {data.package_names?.length > 0 && (
              <div className="pt-2 border-t border-white/5">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">Packages</p>
                <div className="flex flex-wrap gap-1">
                  {(data.package_names as string[]).map((pkg) => (
                    <Badge key={pkg} className="text-[10px] border"
                      style={{ background: "rgba(255,168,0,0.1)", color: "#ffa800", borderColor: "rgba(255,168,0,0.3)" }}>
                      {pkg}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Training percentages */}
            {(data.admin_training_percentage != null || data.diviner_training_percentage != null || data.mystery_training_percentage != null) && (
              <div className="pt-2 border-t border-white/5">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">Training Progress</p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    ["Admin", data.admin_training_percentage],
                    ["Diviner", data.diviner_training_percentage],
                    ["Mystery", data.mystery_training_percentage],
                  ].map(([label, val]) => (
                    <div key={label} className="rounded-lg p-2 text-center"
                      style={{ background: "rgba(255,168,0,0.06)", border: "1px solid rgba(255,168,0,0.15)" }}>
                      <p className="text-[10px] text-muted-foreground">{label}</p>
                      <p className="text-sm font-bold" style={{ color: "#ffa800" }}>{pct(val)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.additional_info && (
              <div className="pt-2 border-t border-white/5">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Additional Info</p>
                <p className="text-xs mt-1 text-muted-foreground leading-relaxed">{data.additional_info}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="py-8 text-center text-sm text-destructive">User not found</div>
        )}
        <CloseButton onClick={onClose} />
      </DialogContent>
    </Dialog>
  );
}

// ── Login Info Dialog ─────────────────────────────────────────────────────────

function LoginInfoDialog({ userId, onClose }: { userId: string | null; onClose: () => void }) {
  const { data: session } = useSession();
  const baseUrl = useBaseUrl();

  const { data, isLoading } = useQuery({
    queryKey: ["user/login-info", userId],
    queryFn: async () => {
      const res = await fetch(`${baseUrl}user/fetch-login-details`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token ?? ""}` },
        body: JSON.stringify({ _id: userId }),
      });
      if (!res.ok) throw new Error("Failed");
      const json = await res.json();
      return (json.results ?? []) as Array<{ email: string; login_time: number; ipInfo?: { city?: string; country?: string; ip?: string; timezone?: string } }>;
    },
    enabled: !!userId && !!session?.token,
  });

  return (
    <Dialog open={!!userId} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md" style={modalStyles()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Info className="size-4 text-[#25aceb]" />
            Login Info
          </DialogTitle>
          <DialogDescription className="text-xs">Recent login history for this user.</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-12 flex justify-center"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>
        ) : data && data.length > 0 ? (
          <div className="space-y-2 max-h-72 overflow-y-auto py-1">
            {data.map((entry, i) => (
              <div key={i} className="rounded-lg p-3 space-y-1"
                style={{ background: "hsl(240 20% 18%)", border: "1px solid hsl(240 20% 26%)" }}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-foreground">{entry.email}</span>
                  <span className="text-[10px] text-muted-foreground">
                    {entry.login_time ? format(new Date(entry.login_time), "MMM dd, yyyy HH:mm") : "—"}
                  </span>
                </div>
                {entry.ipInfo && (
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                    {[
                      ["IP", entry.ipInfo.ip],
                      ["City", entry.ipInfo.city],
                      ["Country", entry.ipInfo.country],
                      ["TZ", entry.ipInfo.timezone],
                    ].map(([label, val]) =>
                      val ? (
                        <span key={label} className="text-[10px] text-muted-foreground">
                          <span className="font-semibold text-foreground/60">{label}:</span> {val}
                        </span>
                      ) : null
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">No login records found.</p>
        )}
        <CloseButton onClick={onClose} />
      </DialogContent>
    </Dialog>
  );
}

// ── Manage Role Dialog ────────────────────────────────────────────────────────

function ManageRoleDialog({
  user,
  onClose,
}: {
  user: { id: string; name: string; currentRole: string } | null;
  onClose: () => void;
}) {
  const { data: session } = useSession();
  const queryClient = useQueryClient();
  const baseUrl = useBaseUrl();
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function updateRole() {
    if (!user || !selected) return;
    setLoading(true);
    try {
      const res = await fetch(`${baseUrl}user/user-role-update`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token ?? ""}` },
        body: JSON.stringify({ _id: user.id, user_type: selected }),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success("Role updated successfully");
      void queryClient.invalidateQueries({ queryKey: ["user/user-list"] });
      onClose();
    } catch {
      toast.error("Failed to update role");
    } finally {
      setLoading(false);
    }
  }

  const effectiveRole = selected ?? user?.currentRole ?? "";

  return (
    <Dialog open={!!user} onOpenChange={(open) => { if (!open) { setSelected(null); onClose(); } }}>
      <DialogContent className="max-w-sm" style={modalStyles()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="size-4 text-[#7C3AED]" />
            Manage Role
          </DialogTitle>
          <DialogDescription className="text-xs">
            Change the role for <span className="font-semibold text-foreground">{user?.name}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1 max-h-64 overflow-y-auto py-1">
          {Object.entries(roleLabel).map(([val, name]) => (
            <button
              key={val}
              type="button"
              onClick={() => setSelected(val)}
              className="w-full text-left flex items-center justify-between px-3 py-2 rounded-lg transition-colors text-sm"
              style={{
                background: effectiveRole === val ? "rgba(124,58,237,0.12)" : "transparent",
                border: effectiveRole === val ? "1px solid rgba(124,58,237,0.4)" : "1px solid transparent",
                color: effectiveRole === val ? "#A78BFA" : "hsl(var(--foreground))",
              }}
            >
              {name}
              {effectiveRole === val && <Check className="size-3.5" />}
            </button>
          ))}
        </div>

        <button
          type="button"
          disabled={!selected || selected === user?.currentRole || loading}
          onClick={() => void updateRole()}
          className="w-full rounded-lg py-2 text-sm font-semibold transition-colors disabled:opacity-40 btn-orange mt-1"
        >
          {loading ? <Loader2 className="size-4 animate-spin mx-auto" /> : "Save Role"}
        </button>
        <CloseButton onClick={() => { setSelected(null); onClose(); }} />
      </DialogContent>
    </Dialog>
  );
}

// ── Training Settings Dialog ──────────────────────────────────────────────────

function TrainingSettingsDialog({
  user,
  onClose,
}: {
  user: { id: string; name: string; sequentialEnabled: boolean } | null;
  onClose: () => void;
}) {
  const { data: session } = useSession();
  const queryClient = useQueryClient();
  const baseUrl = useBaseUrl();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);

  const effectiveEnabled = enabled ?? user?.sequentialEnabled ?? false;

  async function save() {
    if (!user) return;
    setLoading(true);
    try {
      const res = await fetch(`${baseUrl}user/user-update`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token ?? ""}` },
        body: JSON.stringify({ _id: user.id, sequential_training_enabled: effectiveEnabled ? 1 : 0 }),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success("Training settings updated");
      void queryClient.invalidateQueries({ queryKey: ["user/user-list"] });
      onClose();
    } catch {
      toast.error("Failed to update training settings");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={!!user} onOpenChange={(open) => { if (!open) { setEnabled(null); onClose(); } }}>
      <DialogContent className="max-w-sm" style={modalStyles()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="size-4 text-[#16a085]" />
            Training Settings
          </DialogTitle>
          <DialogDescription className="text-xs">
            Configure training settings for{" "}
            <span className="font-semibold text-foreground">{user?.name}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl p-4 space-y-3 mt-1"
          style={{ background: "hsl(240 20% 18%)", border: "1px solid hsl(240 20% 26%)" }}>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold">Sequential Training</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                User must complete lessons in order
              </p>
            </div>
            <button
              type="button"
              onClick={() => setEnabled(!effectiveEnabled)}
              className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
              style={{ background: effectiveEnabled ? "#16a085" : "hsl(240 20% 30%)" }}
            >
              <span className="inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform"
                style={{ transform: effectiveEnabled ? "translateX(18px)" : "translateX(2px)" }} />
            </button>
          </div>
        </div>

        <button
          type="button"
          disabled={loading}
          onClick={() => void save()}
          className="w-full rounded-lg py-2 text-sm font-semibold transition-colors disabled:opacity-40 btn-orange mt-1"
        >
          {loading ? <Loader2 className="size-4 animate-spin mx-auto" /> : "Save Settings"}
        </button>
        <CloseButton onClick={() => { setEnabled(null); onClose(); }} />
      </DialogContent>
    </Dialog>
  );
}

// ── Main Table Component ───────────────────────────────────────────────────────

export function UserListTable() {
  const router = useRouter();
  const { data: session } = useSession();
  const baseUrl = useBaseUrl();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortField, setSortField] = useState("createdon_datetime");
  const [sortType, setSortType] = useState<"asc" | "desc">("desc");
  const [searchCondition, setSearchCondition] = useState<Record<string, unknown>>({});
  const [condition, setCondition] = useState<Record<string, unknown>>({});

  // U-05: Fetch roles dynamically from fetch-role-list; use role_name as display label
  const { data: rolesData } = useQuery({
    queryKey: ["admin/fetch-role-list"],
    queryFn: async () => {
      const res = await fetch(`${baseUrl}admin/fetch-role-list`, {
        headers: { Authorization: `Bearer ${session?.token ?? ""}` },
      });
      const json = await res.json();
      const result = json.result ?? json.results ?? [];
      return (Array.isArray(result) ? result : []) as { role_name: string; user_type: string; slug?: string }[];
    },
    enabled: !!session?.token,
    staleTime: 5 * 60 * 1000,
  });

  // Dialog states
  const [mundaneUser, setMundaneUser] = useState<{ id: string; name: string; access: 0 | 1 | 2 } | null>(null);
  const [previewUserId, setPreviewUserId] = useState<string | null>(null);
  const [loginInfoUserId, setLoginInfoUserId] = useState<string | null>(null);
  const [manageRoleUser, setManageRoleUser] = useState<{ id: string; name: string; currentRole: string } | null>(null);
  const [trainingUser, setTrainingUser] = useState<{ id: string; name: string; sequentialEnabled: boolean } | null>(null);
  // D-09: Per-user Notes
  const [notesUser, setNotesUser] = useState<{ id: string; name: string } | null>(null);

  const { data, isLoading, isFetching } = useApiList<UserRow>("user/user-list", {
    page, limit, sortField, sortType, condition, searchcondition: searchCondition,
    countEndpoint: "user/user-list-count", nestedPagination: true,
  });

  const statusMutation = useApiPost("user/status-change", {
    invalidateKeys: ["user/user-list", "user/user-list-count"],
  });

  // Send welcome mail
  async function sendWelcomeMail(userId: string, userName: string) {
    try {
      const res = await fetch(`${baseUrl}user/resend-welcome-mail`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token ?? ""}` },
        body: JSON.stringify({ _id: userId }),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success(`Welcome mail sent to ${userName}`);
    } catch {
      toast.error("Failed to send welcome mail");
    }
  }

  // Reset password mail
  async function sendResetPasswordMail(userId: string, userName: string) {
    try {
      const res = await fetch(`${baseUrl}user/send-resetpassword-mail`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token ?? ""}` },
        body: JSON.stringify({ _id: userId }),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success(`Password reset mail sent to ${userName}`);
    } catch {
      toast.error("Failed to send reset password mail");
    }
  }

  // Mark as graduate
  async function markAsGraduate(userId: string, userName: string) {
    try {
      const res = await fetch(`${baseUrl}user/user-update`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token ?? ""}` },
        body: JSON.stringify({ _id: userId, is_graduated: 1 }),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success(`${userName} marked as graduate`);
      void queryClient.invalidateQueries({ queryKey: ["user/user-list"] });
    } catch {
      toast.error("Failed to mark as graduate");
    }
  }

  const handleSearch = useCallback((field: string, value: string) => {
    setPage(1);
    setSearchCondition((prev) => {
      if (!value) { const next = { ...prev }; delete next[field]; return next; }
      return { ...prev, [field]: { $regex: value, $options: "i" } };
    });
  }, []);

  const handleFilter = useCallback((field: string, value: string) => {
    setPage(1);
    setSearchCondition((prev) => {
      if (!value) { const next = { ...prev }; delete next[field]; return next; }
      // Status filter uses numeric comparison; role + other filters use exact string match
      // U-07: user_type filter goes to searchcondition (confirmed — this is already correct)
      return { ...prev, [field]: field === "status" ? (value === "1" ? 1 : 0) : value };
    });
  }, []);

  // U-04: Date range filter on createdon_datetime
  const handleDateFilter = useCallback((field: string, start?: string, end?: string) => {
    setPage(1);
    setSearchCondition((prev) => {
      const next = { ...prev };
      if (!start && !end) {
        delete next[field];
        return next;
      }
      const range: Record<string, unknown> = {};
      if (start) range.$gte = new Date(start).getTime();
      if (end) range.$lte = new Date(end + "T23:59:59").getTime();
      return { ...next, [field]: range };
    });
  }, []);

  const handleReset = useCallback(() => {
    setPage(1);
    setSearchCondition({});
  }, []);

  const handleSort = useCallback((field: string, type: "asc" | "desc") => {
    setSortField(field);
    setSortType(type);
  }, []);

  const columns: ColumnDef<UserRow>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
      enableSorting: true,
    },
    {
      accessorKey: "user_type",
      header: "Role",
      cell: ({ row }) => (
        <Badge variant="outline" className="text-[10px]">
          {roleLabel[row.getValue("user_type") as string] ?? row.getValue("user_type")}
        </Badge>
      ),
    },
    {
      accessorKey: "email",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Email" />,
      enableSorting: true,
    },
    {
      accessorKey: "phone",
      header: "Phone",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <DataTableStatusCell
          status={row.original.status}
          onToggle={async () => {
            await statusMutation.mutateAsync({
              data: row.original._id,
              status: row.original.status === 1 ? 0 : 1,
            });
          }}
        />
      ),
    },
    {
      id: "createdon_datetime",
      accessorFn: (row) => row.createdon_datetime ?? row.created_at,
      header: ({ column }) => <DataTableColumnHeader column={column} title="Created" />,
      cell: ({ row }) => {
        const val = row.original.createdon_datetime ?? row.original.created_at;
        return val ? <span className="text-xs text-muted-foreground">{format(new Date(val), "MMM dd, yyyy")}</span> : "—";
      },
      enableSorting: true,
    },
    {
      accessorKey: "last_login_time",
      header: "Last Login",
      cell: ({ row }) => {
        const val = row.getValue("last_login_time") as number;
        return val ? (
          <span className="text-xs text-muted-foreground">{format(new Date(val), "MMM dd, yyyy HH:mm")}</span>
        ) : "—";
      },
    },
    {
      accessorKey: "diviner_mystery_percentage",
      header: "Mystery %",
      cell: ({ row }) => {
        const val = row.original.diviner_mystery_percentage;
        if (val == null) return <span className="text-xs text-muted-foreground">—</span>;
        return (
          <div className="flex items-center gap-1.5">
            <div className="h-1.5 w-16 rounded-full overflow-hidden" style={{ background: "hsl(240 20% 22%)" }}>
              <div
                className="h-full rounded-full"
                style={{ width: `${Math.min(val, 100)}%`, background: "#7C3AED" }}
              />
            </div>
            <span className="text-xs text-muted-foreground">{val}%</span>
          </div>
        );
      },
    },
    {
      id: "actions",
      header: () => <span className="text-xs text-muted-foreground">Actions</span>,
      cell: ({ row }) => {
        const user = row.original;
        const access = (user.mundane_read_write_access ?? 0) as 0 | 1 | 2;
        const isTrainee = user.user_type === "is_trainee";
        const isGraduatable = user.user_type === "is_astrologer" || user.user_type === "is_tarotreader";

        return (
          <div className="flex items-center gap-1">
            {/* Edit */}
            <button onClick={() => router.push(`/admin/users/edit/${user._id}`)} title="Edit"
              className="inline-flex size-7 items-center justify-center rounded transition-colors hover:bg-[#25aceb]/15"
              style={{ color: "#25aceb" }}>
              <Pencil className="size-3.5" />
            </button>

            {/* Preview */}
            <button onClick={() => setPreviewUserId(user._id)} title="Preview"
              className="inline-flex size-7 items-center justify-center rounded transition-colors hover:bg-[#16a085]/15"
              style={{ color: "#16a085" }}>
              <Eye className="size-3.5" />
            </button>

            {/* Mundane Astrology */}
            <button
              onClick={() => setMundaneUser({ id: user._id, name: user.name, access })}
              title={`Mundane Astrology${access > 0 ? ` (${access === 1 ? "Read" : "Write"})` : ""}`}
              className="inline-flex size-7 items-center justify-center rounded transition-colors hover:bg-[#ffa800]/15"
              style={{ color: access > 0 ? "#ffa800" : "hsl(var(--muted-foreground))" }}>
              <Star className={`size-3.5 ${access > 0 ? "fill-current" : ""}`} />
            </button>

            {/* More actions dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button title="More actions"
                  className="inline-flex size-7 items-center justify-center rounded transition-colors hover:bg-white/8 text-muted-foreground hover:text-foreground">
                  <MoreHorizontal className="size-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52" style={{ background: "hsl(240 28% 14%)", border: "1px solid hsl(240 20% 22%)" }}>
                <DropdownMenuItem className="gap-2 cursor-pointer text-xs" onClick={() => void sendWelcomeMail(user._id, user.name)}>
                  <Mail className="size-3.5 text-[#25aceb]" /> Send Welcome Mail
                </DropdownMenuItem>
                <DropdownMenuItem className="gap-2 cursor-pointer text-xs" onClick={() => void sendResetPasswordMail(user._id, user.name)}>
                  <KeyRound className="size-3.5 text-[#ffa800]" /> Reset Password
                </DropdownMenuItem>
                <DropdownMenuSeparator style={{ background: "hsl(240 20% 22%)" }} />
                <DropdownMenuItem className="gap-2 cursor-pointer text-xs"
                  onClick={() => setManageRoleUser({ id: user._id, name: user.name, currentRole: user.user_type })}>
                  <ShieldCheck className="size-3.5 text-[#7C3AED]" /> Manage Role
                </DropdownMenuItem>
                <DropdownMenuItem className="gap-2 cursor-pointer text-xs"
                  onClick={() => setLoginInfoUserId(user._id)}>
                  <Info className="size-3.5 text-[#25aceb]" /> Login Info
                </DropdownMenuItem>
                {/* D-09: Notes per user row */}
                <DropdownMenuItem className="gap-2 cursor-pointer text-xs"
                  onClick={() => setNotesUser({ id: user._id, name: user.name })}>
                  <StickyNote className="size-3.5 text-[#ffa800]" /> Notes
                </DropdownMenuItem>
                <DropdownMenuItem className="gap-2 cursor-pointer text-xs"
                  onClick={() => setTrainingUser({ id: user._id, name: user.name, sequentialEnabled: user.sequential_training_enabled === 1 || user.sequential_training_enabled === true })}>
                  <Settings2 className="size-3.5 text-[#16a085]" /> Training Settings
                </DropdownMenuItem>
                <DropdownMenuItem className="gap-2 cursor-pointer text-xs"
                  onClick={() => router.push(`/admin/users/assign-package/${user._id}`)}>
                  <Package2 className="size-3.5 text-[#ffa800]" /> Assign Package
                </DropdownMenuItem>
                {isTrainee && (
                  <>
                    <DropdownMenuSeparator style={{ background: "hsl(240 20% 22%)" }} />
                    <DropdownMenuItem className="gap-2 cursor-pointer text-xs"
                      onClick={() => router.push(`/admin/conference-room?userId=${user._id}`)}>
                      <Video className="size-3.5 text-[#25aceb]" /> Conference Room
                    </DropdownMenuItem>
                  </>
                )}
                {isGraduatable && (
                  <>
                    <DropdownMenuSeparator style={{ background: "hsl(240 20% 22%)" }} />
                    <DropdownMenuItem className="gap-2 cursor-pointer text-xs"
                      onClick={() => void markAsGraduate(user._id, user.name)}>
                      <GraduationCap className="size-3.5 text-[#16a085]" /> Mark as Graduate
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  // U-05: Build role filter values from API fetch-role-list using role_name as label
  // U-07: user_type filter confirmed to go to searchcondition (via handleFilter → setSearchCondition)
  const roleFilterValues = Array.isArray(rolesData) && rolesData.length > 0
    ? rolesData.map((r) => ({ val: r.user_type ?? r.slug ?? "", name: r.role_name }))
    : Object.entries(roleLabel).map(([val, name]) => ({ val, name }));

  const tableConfig: DataTableConfig = {
    title: "User Management",
    searchFields: [
      { label: "Name", field: "name", placeholder: "Search by name..." },
      { label: "Email", field: "email", placeholder: "Search by email..." },
    ],
    filterFields: [
      {
        label: "Status",
        field: "status",
        values: [
          { val: "1", name: "Active" },
          { val: "0", name: "Inactive" },
        ],
      },
      {
        // U-05: label key is role_name from fetch-role-list
        label: "Role",
        field: "user_type",
        values: roleFilterValues,
      },
    ],
    // U-04: Date range filter on createdon_datetime
    dateFields: [
      { field: "createdon_datetime", startLabel: "Registered From", endLabel: "Registered To" },
    ],
    customButtons: [
      { label: "+ Add User", onClick: () => router.push("/admin/users/add") },
    ],
  };

  if (isLoading) {
    return <div className="py-8 text-center text-muted-foreground"><Loader2 className="size-4 animate-spin inline mr-2" />Loading users...</div>;
  }

  return (
    <>
      <MundaneAstrologyDialog user={mundaneUser} onClose={() => setMundaneUser(null)} />
      <UserPreviewDialog userId={previewUserId} onClose={() => setPreviewUserId(null)} />
      <LoginInfoDialog userId={loginInfoUserId} onClose={() => setLoginInfoUserId(null)} />
      <ManageRoleDialog user={manageRoleUser} onClose={() => setManageRoleUser(null)} />
      <TrainingSettingsDialog user={trainingUser} onClose={() => setTrainingUser(null)} />
      {/* D-09: Per-user Notes dialog */}
      <UserNotesDialog user={notesUser} onClose={() => setNotesUser(null)} />

      <div className="relative">
        {isFetching && (
          <div className="absolute inset-x-0 top-0 flex items-center justify-end px-1 py-0.5 z-10">
            <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
          </div>
        )}
        <DataTable
          columns={columns}
          data={data?.data ?? []}
          totalCount={data?.count ?? 0}
          config={tableConfig}
          page={page}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
          onSearch={handleSearch}
          onSort={handleSort}
          onFilter={handleFilter}
          onDateFilter={handleDateFilter}
          onReset={handleReset}
        />
      </div>
    </>
  );
}
